// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/first-sync-gate/first-sync-percent.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/shared/src/powersync/first-sync-percent.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo aula-courses-app
//     node fleet/fleet.ts check --repo aula-courses-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * The percentage the first-sync UI DISPLAYS, derived from the raw aggregate.
 *
 * The raw `downloadedFraction` from `aggregateStreamProgress` is correct but
 * not displayable as is:
 *
 *   - It goes `null` the instant a download iteration ends (the SDK hides
 *     per-stream progress when nothing is downloading). The bar and the pill
 *     vanished at e.g. 87% — 100% was never shown.
 *   - It goes `null` between iterations too, so the pill blinked out and back.
 *   - The core counts relative to the last checkpoint: when a new checkpoint
 *     lands mid-sync the total grows and the fraction goes BACKWARDS.
 *   - Before the checkpoint arrives `total === 0` reads as a real 0%.
 *   - Rounding shows 100% while operations are still pending (99.6% → 100).
 *
 * The rules below turn it into a monotonic figure that only reaches 100 when
 * the sync is actually complete.
 */

/** Highest percentage shown while operations are still pending. */
const MAX_PENDING_PERCENT = 99;
const COMPLETE_PERCENT = 100;

export interface FirstSyncPercentInput {
  downloadedFraction: number | null;
  totalOperations: number | null;
  hasSynced: boolean;
}

/**
 * Next percentage to display, given the previously displayed one.
 *
 * - `hasSynced` → 100, always.
 * - unknown workload (`null` fraction or `total` 0/null) → keep `previous`
 *   (`null` = indeterminate: render a spinner, not "0%").
 * - otherwise the rounded fraction, capped at 99 until `hasSynced`, and never
 *   below `previous`.
 */
export function nextFirstSyncPercent(
  previous: number | null,
  input: FirstSyncPercentInput,
): number | null {
  if (input.hasSynced) {
    return COMPLETE_PERCENT;
  }
  const fraction = input.downloadedFraction;
  if (fraction === null || !Number.isFinite(fraction) || !input.totalOperations) {
    return previous;
  }
  const raw = Math.round(Math.min(1, Math.max(0, fraction)) * COMPLETE_PERCENT);
  const capped = Math.min(MAX_PENDING_PERCENT, raw);
  return previous === null ? capped : Math.max(previous, capped);
}
